import { mark, timer } from "../state/ai/runMetrics";

// The host rejects a slice larger than this
const SLICE_SIZE = 4194304;

export const XLSX_MEDIA_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export interface XlsxSnapshot {
  bytes: Uint8Array;
  fileName: string;
}

const openWorkbook = () => new Promise<Office.File>((resolve, reject) => {
  Office.context.document.getFileAsync(
    Office.FileType.Compressed,
    { sliceSize: SLICE_SIZE },
    r => {
      if (r.status === Office.AsyncResultStatus.Succeeded) {
        resolve(r.value);
        return;
      }
      reject(new Error(r.error?.message || "The workbook could not be opened for reading."));
    }
  );
});

const readSlice = (file: Office.File, index: number) => new Promise<Uint8Array>((resolve, reject) => {
  file.getSliceAsync(index, r => {
    if (r.status === Office.AsyncResultStatus.Succeeded) {
      resolve(new Uint8Array(r.value.data));
      return;
    }
    reject(new Error(r.error?.message || `Part ${index + 1} of the workbook could not be read.`));
  });
});

const getWorkbookFileName = (): string => {
  const url = Office.context.document.url || "";
  const base = url.split(/[\/]/).pop()?.split("?")[0] || "";

  if (!base) return "workbook.xlsx";

  return /\.xlsx$/i.test(base) ? base : `${base.replace(/\.[^.]+$/, "")}.xlsx`;
};

const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunk = 0x8000;

  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }

  return btoa(binary);
};

export const getFileAsXlsx = async (skill?: string): Promise<XlsxSnapshot> => {

  if (typeof Office === "undefined" || !Office.context?.document) {
    throw new Error("Not running inside an Office document.");
  }

  const openDone = timer(skill, "workbook:getFileAsync");
  const file = await openWorkbook();

  openDone({ sliceCount: file.sliceCount, size: file.size });

  try {
    const sliceDone = timer(skill, "workbook:slices");

    const slices = await Promise.all(
      Array.from({ length: file.sliceCount }, (_, i) => readSlice(file, i))
    );

    const size = slices.reduce((acc, s) => acc + s.length, 0);

    sliceDone({ count: slices.length, bytes: size });

    const joinDone = timer(skill, "workbook:join");

    const bytes = new Uint8Array(size);
    let offset = 0;

    for (const s of slices) {
      bytes.set(s, offset);
      offset += s.length;
    }

    joinDone({ bytes: size });

    return {
      bytes,
      fileName: getWorkbookFileName()
    };
  }
  finally {
    const closeDone = timer(skill, "workbook:close");
    file.closeAsync();
    closeDone();
  }
};

export const getWorkbookContainerBlock = async (skill?: string): Promise<Record<string, any>> => {

  const readDone = timer(skill, "workbook:read");
  const { bytes, fileName } = await getFileAsXlsx(skill);
  readDone({ bytes: bytes.length, fileName });

  const encodeDone = timer(skill, "workbook:base64");
  const data = toBase64(bytes);
  encodeDone({ bytes: bytes.length, base64Length: data.length });

  return {
    type: "document",
    source: {
      type: "base64",
      media_type: XLSX_MEDIA_TYPE,
      data
    },
    title: fileName,
    _route: "container"
  };
};

export const tryGetWorkbookContainerBlock = async (skill?: string): Promise<Record<string, any> | null> => {
  try {
    return await getWorkbookContainerBlock(skill);
  }
  catch (err) {
    mark(skill, "workbook:error", { message: err instanceof Error ? err.message : "unknown" });
    console.error("Could not attach the workbook:", err);
    return null;
  }
};
